import { Modal, useModals } from 'almanac-ui/Modal'
import { useEffect, useState } from 'react'
import { PressableBrandAICard } from 'screens/BrandKits/components/BrandAICard/BrandAICard'
import Styles from '../DesignWizardField.module.scss'
import { HighDetailedVoice } from 'blaze-ui/icons/24'
import { VoiceData } from 'hooks/useBrandVoices'
import { BrandVoicePreview } from 'screens/BrandKits/components/BrandVoice/BrandVoiceCard/BrandVoicePreview'
import { noop } from 'lodash'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import { Plus } from 'blaze-ui/icons'
import { useOpenBrandVoiceModal } from 'screens/BrandKits/components/BrandVoice/useOpenBrandVoiceModal'
import { AnalysingContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/AnalysingContentModal'
import { EditVoiceModal } from 'screens/BrandKits/components/BrandVoice/EditVoiceModal'
import { NewBrandVoiceCard } from 'screens/BrandKits/components/BrandVoice/NewBrandVoiceCard/NewBrandVoiceCard'

interface Props {
  voices: VoiceData[]
  onVoiceSelect: (brandVoiceId: number) => void
  onTonesSelect: (tones: Tone[]) => void
  initialVoice?: VoiceData
  initialTones: Tone[]
}

export const BrandVoicesModal = ({ voices, onVoiceSelect, onTonesSelect, initialVoice, initialTones = [] }: Props) => {
  const [selectedVoice, setSelectedVoice] = useState<number | undefined>()
  const [selectedTones, setSelectedTones] = useState<Tone[]>(initialTones)
  const { openModal, closeModal, closeModals, closeStack } = useModals()
  const openBrandVoiceModal = useOpenBrandVoiceModal()

  const tonesDisabled = !!selectedVoice
  const voiceDisabled = selectedTones.length > 0

  useEffect(() => setSelectedVoice(initialVoice?.id), [initialVoice?.id])
  useEffect(() => {
    if (!!selectedVoice) setSelectedTones([])
  }, [selectedVoice])

  const openAddVoiceModal = () => {
    openBrandVoiceModal(
      {
        onAnalyse: (analyseProps) =>
          openModal(AnalysingContentModal, {
            ...analyseProps,
            onNext: (editProps) =>
              openModal(EditVoiceModal, {
                ...editProps,
                onBack: () => {
                  closeModals(2)
                },
                onCreate: ({ voiceId }) => {
                  closeStack()
                  onVoiceSelect(voiceId)
                },
              }),
          }),
      },
      { newStack: true },
    )
  }

  const handleSubmit = () => {
    if (selectedVoice) {
      onVoiceSelect(Number(selectedVoice))
    } else {
      onTonesSelect(selectedTones)
    }
    closeModal()
  }

  return (
    <Modal.Root onPressOutside={closeModal}>
      <Modal.Header
        title={
          <div className={Styles.title}>
            <HighDetailedVoice />
            Brand Voices
          </div>
        }
        actions={
          <Button variant="tertiary" frontIcon={Plus} onPress={() => openAddVoiceModal()}>
            Create New
          </Button>
        }
      />
      <Modal.Content>
        <Heading level={5}>Select a brand voice</Heading>
        <div className={Styles.voices}>
          <div className={cx(Styles.grid, { [Styles.disabledSection]: voiceDisabled })}>
            {voices.map((brandVisualVoice, i) => {
              return (
                <div key={brandVisualVoice.id} className={Styles.checkboxItem}>
                  {brandVisualVoice.id === selectedVoice && (
                    <div className={Styles.checkbox}>
                      <Checkbox size="lg" isSelected />
                    </div>
                  )}
                  <PressableBrandAICard
                    key={brandVisualVoice.id}
                    size="md"
                    withShadow
                    lineClamp={1}
                    preview={() => (
                      <BrandVoicePreview
                        voice={brandVisualVoice}
                        onEmojiSelect={noop}
                        onRemoveEmoji={noop}
                        isEmojiMenuOpen={false}
                        onEmojiMenuOpenChange={noop}
                        size="md"
                      />
                    )}
                    onPress={() =>
                      setSelectedVoice(brandVisualVoice.id === selectedVoice ? undefined : brandVisualVoice.id)
                    }
                    isSelected={selectedVoice === brandVisualVoice.id}
                    title={brandVisualVoice.voice_params.name}
                  />
                </div>
              )
            })}
            {(voices.length === 0 || voices.length % 3 !== 0) && (
              <NewBrandVoiceCard size="md" title="Create New Brand Voice" />
            )}
          </div>
        </div>

        <Heading level={5}>Or select up to 3 tones</Heading>

        <div className={cx(Styles.grid, Styles.tones, { [Styles.disabledSection]: tonesDisabled })}>
          {Object.values(Tone).map((tone) => (
            <Checkbox
              size="lg"
              key={tone}
              className={Styles.checkbox}
              isSelected={selectedTones.includes(tone)}
              isDisabled={!selectedTones.includes(tone) && selectedTones.length === 3}
              onChange={() => {
                setSelectedVoice(undefined)

                const tonesSet = new Set(selectedTones)
                tonesSet.has(tone) ? tonesSet.delete(tone) : tonesSet.add(tone)
                setSelectedTones(Array.from(tonesSet).slice(0, 3))
              }}
            >
              <Text variant="smallList" color="var(--dark-80)">
                {tone}
              </Text>
            </Checkbox>
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} variant="tertiary" size="lg">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={handleSubmit} size="lg" variant="primary">
            {!selectedVoice && !selectedTones.length
              ? "Don't Use Voice"
              : `Use Selected ${selectedVoice ? 'Voice' : 'Tones'}`}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
